import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import { appConfig } from '../config/app.config';
import { Business } from '../models/Businesses';
import { AlertService } from '../util/alert.service';
import { ToastService } from '../util/toast.service';
import { Employee } from './../models/Employees';
import { LocalDbService } from './localDb.service';
import { BackEndService } from './backEnd.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  business: Business = new Business();

  loggedInEmployee: Employee;
  windowRef: any;
  subCommunity;
  // Subject to notify when user logs in
  public loginSuccess$ = new Subject<Employee>();

  private _loggedInPromise: Promise<any> | null = null;
  private _businessPromises: { [key: string]: Promise<any> } = {};

  constructor(
    private toast: ToastService,
    private localDb: LocalDbService,
    private backEnd: BackEndService,
    private alert: AlertService,
    private router: Router,
  ) { }

  private parseIfString(data: any) {
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return data; }
    }
    return data;
  }
  async getBusiness(businessId) {
    if (this.business?.id === businessId) return this.business;

    if (this._businessPromises[businessId]) {
      return this._businessPromises[businessId];
    }

    this._businessPromises[businessId] = (async () => {
      try {
        // Try local storage first
        const localBusiness = await this.localDb.localGet('business');
        if (localBusiness && localBusiness.id === businessId) {
          this.business = localBusiness;
          // Trigger background sync but return local version now
          this.syncBusiness(businessId);
          return localBusiness;
        }

        return await this.syncBusiness(businessId);
      } catch {
        return null;
      } finally {
        delete this._businessPromises[businessId];
      }
    })();

    return this._businessPromises[businessId];
  }

  private async syncBusiness(businessId) {
    try {
      const b: any = await this.backEnd.getSingle("business", [["id", businessId]]);
      if (!b) return null;

      b.category = this.parseIfString(b.category);
      b.tag = this.parseIfString(b.tag);
      b.config = this.parseIfString(b.config);

      this.business = b;
      await this.localDb.localPost('business', b);
      return b;
    } catch {
      return null;
    }
  }

  async verifyOTP(otp, mobile) {
    try {
      const credential = await this.backEnd.searchObject(
        "user_whatsapp",
        [["mobile", mobile], ["otp", otp], ["active", true]]
      );

      if (!credential?.length) {
        this.toast.error("Invalid OTP");
        return null;
      }
      credential[0].active = false;
      await this.backEnd.saveObject("user_whatsapp", credential[0]);

      return await this.login(mobile);

    } catch (err) {
      this.toast.error(err);
      return null;
    }
  }

  signOut(keepInvitee: boolean = false, reload: boolean = false, silent: boolean = false) {
    const doLogout = async () => {
      console.log("🛠️ AuthService: doLogout started (silent=" + silent + ")");
      if (this.business && this.business.id) {
        await this.localDb.localPost('business', this.business);
      }
      if (this.loggedInEmployee?.user?.user_pd?.sub_community) {
        await this.localDb.localPost('sub_community', this.loggedInEmployee.user.user_pd.sub_community);
      }
      await this.backEnd.signOut();
      if (!keepInvitee) {
        await this.localDb.localDelete('invitee');
      }
      this.loggedInEmployee = null;
      console.log("🏃 Navigating to /register...");
      await this.router.navigate(['/register'], { replaceUrl: true });
      if (reload) {
        console.log("♻️ Reloading page (manual logout requested)...");
        window.location.reload();
      }
    };
    if (silent) {
      doLogout();
    } else {
      this.alert.showAlert('Logout', 'Are you sure you want to logout?').then(() => {
        doLogout();
      });
    }
  }

  async getLoggedInEmployee() {
    if (this.loggedInEmployee != null) {
      return this.loggedInEmployee;
    }
    if (this._loggedInPromise) {
      return this._loggedInPromise;
    }

    this._loggedInPromise = (async () => {
      try {
        const emp = await this.localDb.localGet(appConfig.STORAGE_OBJECTS.LOGGED_IN_EMPLOYEE) as Employee;
        if (emp) {
          this.loggedInEmployee = emp;
          this.getBusiness(emp.business_id);
          this.getSingleEmployee(emp.id).then(async (webEmp) => {
            const freshEmp = webEmp as Employee;
            if (freshEmp) {
              this.loggedInEmployee = freshEmp;
              await this.localDb.localPost(appConfig.STORAGE_OBJECTS.LOGGED_IN_EMPLOYEE, freshEmp);
              this.loginSuccess$.next(freshEmp);
            }
          });
          return emp;
        }

        const empId = await this.localDb.localGet("empId");
        if (empId) {
          const webEmp = await this.getSingleEmployee(empId) as Employee;
          if (webEmp) {
            await this.localDb.localPost(appConfig.STORAGE_OBJECTS.LOGGED_IN_EMPLOYEE, webEmp);
            return webEmp;
          }
        }

        return null;
      } catch (err) {
        console.error("AuthService: getLoggedInEmployee error:", err);
        return null;
      } finally {
        this._loggedInPromise = null;
      }
    })();
    return this._loggedInPromise;
  }

  getSingleEmployee(empId) {
    return new Promise(resolve => {
      this.backEnd.getSingle(appConfig.STORAGE_TABLE.EMPLOYEE, [["id", empId]], "user:user_id(*, user_pd:user_pd_user_id_fkey(*))").then((webEmp: any) => {
        if (webEmp) {
          if (webEmp.user.user_pd && webEmp.user.user_pd.length > 0) {
            webEmp.user.user_pd = webEmp.user.user_pd[0];
          }
          if (webEmp.user.dob && (typeof webEmp.user.dob === 'string' || webEmp.user.dob instanceof String)) {
            webEmp.user.dob = new Date(webEmp.user.dob);
          }
          if (typeof webEmp.access === 'string' || webEmp.access instanceof String) {
            webEmp.access = JSON.parse(webEmp.access + "");
          }
          if (typeof webEmp.assigned_area === 'string' || webEmp.assigned_area instanceof String) {
            webEmp.assigned_area = JSON.parse(webEmp.assigned_area + "");
          }
          if (typeof webEmp.user.user_pd.achievement === 'string' || webEmp.user.user_pd.achievement instanceof String) {
            webEmp.user.user_pd.achievement = JSON.parse(webEmp.user.user_pd.achievement + "");
          }
          if (typeof webEmp.user.user_pd.tag === 'string' || webEmp.user.user_pd.tag instanceof String) {
            webEmp.user.user_pd.tag = JSON.parse(webEmp.user.user_pd.tag + "");
          }
          this.loggedInEmployee = webEmp;
          // console.log(this.loggedInEmployee);
          resolve(this.loggedInEmployee);
        } else {
          resolve(null);
        }
      })
    })
  }

  async login(mobile) {
    const webEmp: any = await this.backEnd.getSingle(
      appConfig.STORAGE_TABLE.EMPLOYEE,
      [["user.user_pd.mobile", mobile]],
      "user:user_id!inner(*, user_pd:user_pd_user_id_fkey!inner(*))"
    );
    if (!webEmp) return null;
    if (webEmp.user.user_pd?.length) {
      webEmp.user.user_pd = webEmp.user.user_pd[0];
    }
    await this.setLoggedInEmp(webEmp);
    return this.loggedInEmployee;
  }

  async setLoggedInEmp(emp) {
    this.loggedInEmployee = emp;
    await this.localDb.localPost(appConfig.STORAGE_OBJECTS.LOGGED_IN_EMPLOYEE, this.loggedInEmployee);
    this.backEnd.rowPerPage = 200; // or some other way to sync state if needed
    this.loginSuccess$.next(emp);
  }

  sendOTP(mobile, url, name, address, invitee, inviteeAddress, memberType) {
    console.log('url', url)
    return new Promise((resolve) => {
      this.backEnd.searchObject("user_whatsapp", [["mobile", mobile], ["active", true]]).then((uw) => {
        console.log('user_whatsapp', uw)
        if (!uw || uw.length == 0) {
          url = url.substring(0, url.indexOf("login") + 5);
          console.log('url2', url)
          let message = " is your OTP.  Hello " + name + " \n\n " + address + ".\n\n Thank you for joining Vanshavali. You are Invited as " + memberType + " by " + invitee + " from " + inviteeAddress + ".\n\n Vanshavali is designed and developed to Create and View our Entire Family Tree . For more help click the Youtube Link. Thank you for joining with us,\n\n " + this.business.app;
          this.backEnd.saveObject("user_whatsapp", { mobile: mobile, url: url, message: message }).then(() => {
            console.log('url3', url)
            resolve(true);
          })
        } else {
          this.backEnd.deleteObject("user_whatsapp", uw[0].id).then(() => {
            this.sendOTP(mobile, url, name, address, invitee, inviteeAddress, memberType).then(() => {
              resolve(true);
            });
          });
        }
      })
    })
  }

  comingSoon() {
    this.alert.showAlert("Coming Soon", "This feature is coming soon. Please check back later.");
  }

  // async googleLogin() {
  //   this.backEnd.signInWithGoogle();
  // }


  async googleLogin() {
    try {
      const { environment } = await import('src/environments/environment');
      const { Capacitor } = await import('@capacitor/core');

      let idToken: string;

      if (Capacitor.isNativePlatform()) {
        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
        // console.log('[AuthService] Initiating Native Google Login...');
        await GoogleAuth.initialize({
          clientId: '618275083016-d2j33rhu1gfk530378pdt3o279vv8ost.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
        });

        const googleUser = await GoogleAuth.signIn();
        const googleIdToken = googleUser?.authentication?.idToken;
        const googleAccessToken = googleUser?.authentication?.accessToken;
        console.log('[AuthService] Google signIn success. Has idToken:', !!googleIdToken, 'Has accessToken:', !!googleAccessToken);

        if (!googleIdToken && !googleAccessToken) {
          this.toast.error('Google sign-in failed: no credentials received');
          return null;
        }
        const { initializeApp: initApp, getApps: getAppsList } = await import('firebase/app');
        if (getAppsList().length === 0) {
          initApp(environment.firebase);
        }
        const { GoogleAuthProvider, signInWithCredential, getAuth } = await import('firebase/auth');
        const credential = GoogleAuthProvider.credential(googleIdToken, googleAccessToken);
        const firebaseResult = await signInWithCredential(getAuth(), credential);
        idToken = await firebaseResult.user.getIdToken();
        // console.log('[AuthService] Firebase ID Token obtained:', !!idToken);

      } else {
        const { initializeApp, getApps } = await import('firebase/app');
        if (getApps().length === 0) {
          initializeApp(environment.firebase);
        }

        const { GoogleAuthProvider, signInWithPopup, getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const provider = new GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        // console.log('[AuthService] Opening Google popup...');
        const result = await signInWithPopup(auth, provider);
        idToken = await result.user.getIdToken();
      }

      if (idToken) {
        // console.log('[AuthService] Got Google idToken, sending to backend...');
        try {
          const response: any = await this.backEnd.signInWithGoogleFirebase(idToken);
          if (response?.jwt) {
            await this.backEnd.setSession(response);
            // console.log('[AuthService] Backend login successful');
            return response;
          } else {
            // console.error('[AuthService] Backend response missing JWT:', JSON.stringify(response));
            this.toast.error('Google login failed (No JWT returned from server)');
            return null;
          }
        } catch (serverError: any) {
          console.error('[AuthService] Backend verification error (full):', JSON.stringify(serverError));
          const msg = serverError?.message || serverError?.error?.message || JSON.stringify(serverError);
          this.toast.error('Login error: ' + msg);
          return null;
        }
      } else {
        this.toast.error('Could not obtain Google token.');
        return null;
      }
    } catch (error: any) {
      // console.error('[AuthService] Google login catch-all error:', error);
      if (error?.message !== 'unauthenticated' && error?.message !== 'User cancelled') {
        const errorMsg = error?.message || (typeof error === 'object' ? JSON.stringify(error) : error);
        this.toast.error('Google login failed: ' + errorMsg);
      }
      return null;
    }

  }
}